import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

const client = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add Bearer token auth
client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const apiKey = import.meta.env.VITE_API_KEY
    if (apiKey) {
      config.headers.Authorization = `Bearer ${apiKey}`
    }
    return config
  },
  (error: AxiosError) => Promise.reject(error)
)

// Unwrap axios response body (NestJS returns { data, timestamp })
client.interceptors.response.use(
  (response) => response.data,
  (error: AxiosError) => {
    console.error('API Error:', error.response?.data || error.message)
    return Promise.reject(error)
  }
)

// Helper: extract .data from NestJS envelope { data, timestamp }
function unwrap<T>(response: any): T {
  return response.data
}

// --- Products ---

const products = {
  async list() {
    return unwrap<any[]>(await client.get('/products'))
  },
  async get(id: number) {
    return unwrap<any>(await client.get(`/products/${id}`))
  },
  async getBySubdomain(subdomain: string) {
    return unwrap<any>(await client.get(`/products/by-subdomain/${subdomain}`))
  },
}

// --- Feedback ---

interface FeedbackListParams {
  productId?: number
  status?: string
  limit?: number
}

interface FeedbackSearchParams {
  productId?: number
  limit?: number
}

const feedback = {
  async list(params?: FeedbackListParams) {
    return unwrap<any[]>(await client.get('/feedback', { params }))
  },
  async search(q: string, params?: FeedbackSearchParams) {
    return unwrap<any[]>(await client.get('/feedback/search', { params: { q, ...params } }))
  },
  async get(id: number) {
    return unwrap<any>(await client.get(`/feedback/${id}`))
  },
  async getActivity(id: number) {
    return unwrap<any[]>(await client.get(`/feedback/${id}/activity`))
  },
  async create(data: Record<string, unknown>) {
    return unwrap<any>(await client.post('/feedback', data))
  },
  async updateStatus(id: number, data: { status: string; actorUserId: string; actorName: string }) {
    return unwrap<any>(await client.patch(`/feedback/${id}/status`, data))
  },
  async assign(id: number, data: { assigneeId: string; actorUserId: string; actorName: string }) {
    return unwrap<any>(await client.patch(`/feedback/${id}/assign`, data))
  },
  async bulkUpdateStatus(data: { feedbackIds: number[]; status: string; actorUserId: string; actorName: string }) {
    return unwrap<any>(await client.patch('/feedback/bulk/status', data))
  },
  async bulkAssign(data: { feedbackIds: number[]; assigneeId: string; actorUserId: string; actorName: string }) {
    return unwrap<any>(await client.patch('/feedback/bulk/assign', data))
  },
}

// --- Notes ---

const notes = {
  async list(feedbackId: number) {
    return unwrap<any[]>(await client.get(`/feedback/${feedbackId}/notes`))
  },
  async create(feedbackId: number, data: { authorUserId: string; authorName: string; content: string }) {
    return unwrap<any>(await client.post(`/feedback/${feedbackId}/notes`, data))
  },
  async update(noteId: number, data: { content: string }) {
    return unwrap<any>(await client.patch(`/notes/${noteId}`, data))
  },
  async remove(noteId: number) {
    return unwrap<any>(await client.delete(`/notes/${noteId}`))
  },
}

// --- Analysis ---

const analysis = {
  async preview(data: { transcript: string }) {
    return unwrap<any>(await client.post('/analysis/preview', data))
  },
  async reanalyze(id: number) {
    return unwrap<any>(await client.post(`/analysis/${id}/reanalyze`))
  },
  async reanalyzeAll() {
    return unwrap<any>(await client.post('/analysis/reanalyze-all'))
  },
}

// --- Team Members ---

const teamMembers = {
  async list() {
    return unwrap<any[]>(await client.get('/team-members'))
  },
  async getByEmail(email: string) {
    return unwrap<any>(await client.get(`/team-members/by-email/${encodeURIComponent(email)}`))
  },
}

// --- Admin ---

const admin = {
  async getStats(productId?: number) {
    const params = productId ? { productId } : undefined
    return unwrap<any>(await client.get('/admin/stats', { params }))
  },
  async getTopPriority(productId?: number, limit?: number) {
    const params: Record<string, any> = {}
    if (productId) params.productId = productId
    if (limit) params.limit = limit
    return unwrap<any[]>(await client.get('/admin/top-priority', { params }))
  },
  async getTrends(productId?: number, days?: number) {
    const params: Record<string, any> = {}
    if (productId) params.productId = productId
    if (days) params.days = days
    return unwrap<any[]>(await client.get('/admin/trends', { params }))
  },
  async getProductBreakdown() {
    return unwrap<any[]>(await client.get('/admin/product-breakdown'))
  },
}

// --- LLM ---

const llm = {
  async getUsageStats() {
    return unwrap<any>(await client.get('/llm/usage-stats'))
  },
}

// --- Storage ---

const storage = {
  async getUploadUrl(data: { filename: string; contentType: string; folder?: string }) {
    return unwrap<{ url: string; key: string }>(await client.post('/storage/upload-url', data))
  },
  async getFileUrl(key: string) {
    return unwrap<{ url: string }>(await client.get('/storage/file-url', { params: { key } }))
  },
}

// --- Export ---

const apiClient = {
  products,
  feedback,
  notes,
  analysis,
  teamMembers,
  admin,
  llm,
  storage,
}

export default apiClient
